import { useApi } from '@src/app/context/Api';
import fields from '@src/entities/ActionPath/includes/fields.json';
import { fieldValidator } from '@src/shared/includes/validation';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useDebounce } from '@src/shared/ui/hooks';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { isEmpty, isUndefined } from 'lodash';
import React, { useCallback, useEffect } from 'react';

import { Styles } from './includes/styles';

const { name: fieldName } = fields;

/**
 * Modal: Duplication
 *
 * Prompts the user with a popup window asking them to specify a new action path name before duplicating.
 *
 * @param actionPath
 * @param handleDuplication
 * @param isOpen
 * @param refetch
 * @param setActionPath
 * @param setIsOpen
 * @returns {Element}
 * @constructor
 */
export function Duplication({
  actionPath = {},
  handleDuplication = async () => {},
  isOpen = false,
  refetch = () => {},
  setActionPath = () => {},
  setIsOpen = () => {},
}) {
  const form = useForm({ defaultValues: { name: actionPath?.name } });
  const name = form.getValues('name');

  const router = useRouter();

  const { useApiQuery } = useApi('ActionPath');

  const [GetValidActionPathName, { data }] = useApiQuery('GetValidActionPathName');

  /**
   * handleDuplicate
   *
   * Sends the api request to duplicate the action path.
   *
   * @returns {Promise<void>}
   */
  const handleDuplicate = async () => {
    setIsOpen(false);
    setActionPath({});
    const response = await handleDuplication({ id: actionPath?.id, name }, refetch);
    const mode = response?.manualAssociation === true ? 'manual' : 'automatic';
    const id = response?.id;
    router.navigate(`/action-paths/${id}/edit/${mode}/draft/step/1`);
  };

  /**
   * handleClose
   *
   * Highlights the row in the table for the action path which was requested to activate, but the user backed out. The
   * goal is to make it easy to identify which row in the table the user attempted to take action on, in-case they
   * change their mind and want to continue with the same one again.
   *
   * @returns {Promise<void>}
   */
  const handleClose = async () => {
    setIsOpen(false);
    setActionPath({});
    router.navigate(`/`);
    router.navigate(`/action-paths?highlight=${actionPath?.id}`);
  };
  /**
   * sendRequest
   *
   * Makes a request to a remote API to validateCriteria whether the user provided action path name exists or not.
   *
   * @type {(function(*): Promise<void>)|*}
   */
  const sendRequest = useCallback(async () => {
    if ((await form?.trigger('name')) === true) {
      GetValidActionPathName({ name });
    }
  }, [name]);

  useEffect(() => {
    if (!isUndefined(data)) {
      const isValid = data;

      if (isValid === true) {
        form?.clearErrors('name');
      }

      if (isValid === false) {
        form?.setError('name', { message: 'Name already in use.', type: 'custom' }, { shouldFocus: true });
      }
    }
  }, [data]);

  const debouncedRequest = useDebounce(sendRequest, 500);

  useEffect(() => {
    if (name === actionPath?.name) {
      form?.setError('name', { message: 'Name already in use.', type: 'custom' }, { shouldFocus: true });
    }
  }, [name, actionPath?.name]);

  return (
    <ErrorHandler location="src/features/ActionPaths/Duplication/Duplication.jsx">
      <FormProvider autoComplete="off" highlighted state={form}>
        <Modal
          footer={
            <Modal.Section>
              <Divider height={1} />
              <Layout.Group alignLayout="right">
                <Button onClick={handleClose} variant="outline">
                  Cancel
                </Button>
                <Button
                  isDisabled={!isEmpty(form?.formState?.errors) || name === actionPath?.name}
                  onClick={handleDuplicate}
                  variant="solid"
                >
                  Duplicate
                </Button>
              </Layout.Group>
            </Modal.Section>
          }
          isOpen={isOpen}
          onClose={handleClose}
          title="Confirm Duplication"
        >
          <Styles>
            <Modal.Section>
              <TextInput isDisabled label="Current Action Path Name" value={actionPath?.name} />
              <TextInput
                {...fieldName}
                hideLabel={false}
                label="New Action Path Name"
                onChange={async (event) => {
                  if (event?.target?.value?.length >= 3) {
                    if ((await form?.trigger('name')) === true) {
                      form?.register('name');
                      form?.setError(
                        'name',
                        { message: 'Verifying name is available...', type: 'custom' },
                        { shouldFocus: true }
                      );
                    }
                    debouncedRequest();
                  }
                }}
                validators={{
                  ...fieldName.validators,
                  ...{
                    validate: {
                      customValidator: (value) => {
                        return fieldValidator(fieldName, value);
                      },
                    },
                  },
                }}
              />
            </Modal.Section>
          </Styles>
        </Modal>
      </FormProvider>
    </ErrorHandler>
  );
}
